import axios from 'axios';
import { Invoice } from '../../domain/entities/Invoice';
import { TokenCache } from '../../infrastructure/services/TokenCache';
import { CryptoInterceptor } from '../../infrastructure/api/CryptoInterceptor';

/**
 * Servicio para gestionar el formulario de pago
 */
export class PaymentFormService {
  private baseUrl: string;
  private axiosInstance;
  private tokenCache: TokenCache;
  private cryptoInterceptor: CryptoInterceptor;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '';
    
    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY
      }
    });

    // Inicializar el interceptor de encriptación
    this.cryptoInterceptor = new CryptoInterceptor();
    
    // Configurar interceptores para encriptar/desencriptar automáticamente
    this.axiosInstance.interceptors.request.use(
      this.cryptoInterceptor.requestInterceptor,
      error => Promise.reject(error)
    );
    
    this.axiosInstance.interceptors.response.use(
      this.cryptoInterceptor.responseInterceptor,
      this.cryptoInterceptor.errorInterceptor
    );

    // Obtenemos la instancia única del servicio de caché
    this.tokenCache = TokenCache.getInstance();
  }

  /**
   * Obtiene un token de formulario para procesar un pago
   * @param invoice - Datos de la factura
   * @returns Token del formulario de pago
   */
  async getFormToken(invoice: Invoice): Promise<string> {
    if (!invoice || !invoice.id) {
      throw new Error('Información de factura no válida');
    }

    const invoiceId = invoice.id.toString();
    
    // Usamos el TokenCache para gestionar la solicitud y el almacenamiento en caché
    try {
      
      // La función getToken del TokenCache se encarga de:
      // 1. Verificar si hay un token válido en caché y devolverlo
      // 2. Verificar si hay una solicitud en curso y reutilizarla
      // 3. Realizar una nueva solicitud si es necesario
      return await  this.fetchFormToken(invoice);
      
    } catch (error) {
      console.error('Error al obtener el token del formulario:', error);
      throw new Error('No se pudo obtener el token del formulario de pago');
    }
  }

  /**
   * Método privado para realizar la solicitud HTTP al backend
   * @param invoice - Datos de la factura
   * @returns Token del formulario de pago
   */
  private async fetchFormToken(invoice: Invoice): Promise<string> {
    try {

      // Normalizar la información del invoice para enviarla al backend
      const invoiceData = {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        clientName: invoice.clientName,
        clientId: invoice.clientId.toString(),
        totalAmount: invoice.totalAmount,
        total: invoice.total,
        paymentInterest: invoice.paymentInterest,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
        status: invoice.status,
        description: invoice.description,
        fideicomiso: invoice.fideicomiso,
        office: invoice.office,
        prefijo: invoice.prefijo,
        numeroFactura: invoice.numeroFactura,
        fechaFactura: invoice.fechaFactura,
        secuencialDetalle: invoice.secuencialDetalle
      };
      
      
      // Realizar la solicitud al backend
      const response = await this.axiosInstance.post('/SolicitarToken', invoiceData);
     
      
      // Estrategia directa para extraer el token real
      const extractFormToken = function(data: any): string | null {
        if (!data) return null;
        
        // Intentar estas rutas primero
        if (data.data?.formToken) return data.data.formToken;
        
        // Extraer un token con formato conocido si existe en cualquier cadena
        const jsonStr = JSON.stringify(data);
        const tokenMatch = jsonStr.match(/formToken":\s*"([\w\d]{20,})"/);
        if (tokenMatch && tokenMatch[1]) {
          return tokenMatch[1];
        }
        
        // Recursivamente buscar en propiedades anidadas
        if (typeof data === 'object') {
          // Primero revisar la propiedad answer.formToken que sabemos que existe
          if (data.answer?.formToken) {
            if (typeof data.answer.formToken === 'string') {
              // Si es string, ver si contiene un token codificado o es el token final
              if (data.answer.formToken.startsWith('{')) {
                try {
                  // Intentar parsear si parece JSON
                  const nestedJson = JSON.parse(data.answer.formToken);
                  const nestedToken = extractFormToken(nestedJson);
                  if (nestedToken) return nestedToken;
                } catch (e) {
                  // Si falla, comprobar si contiene un patrón de token
                  const innerMatch = data.answer.formToken.match(/formToken":\s*"([\w\d]{20,})"/);
                  if (innerMatch && innerMatch[1]) return innerMatch[1];
                }
              } else if (/^[\w\d]{20,}$/.test(data.answer.formToken)) {
                // Si parece ser directamente un token (alfanumérico largo)
                return data.answer.formToken;
              }
            }
          }
          
          // Buscar recursivamente en todas las propiedades
          for (const key in data) {
            if (key === 'formToken' && typeof data[key] === 'string' && 
                /^[\w\d]{20,}$/.test(data[key])) {
              return data[key];
            }
            
            const value = data[key];
            if (typeof value === 'object' && value !== null) {
              const result = extractFormToken(value);
              if (result) return result;
            }
          }
        }
        
        return null;
      };
      
      // Extraer el token
      const token = extractFormToken(response.data);
      
      if (token) {
        return token;
      }
      
      console.error('No se pudo encontrar el token en la respuesta:', response.data);
      throw new Error('Formato de respuesta inesperado: no se pudo encontrar el token');
    } catch (error: unknown) {
      console.error('Error completo al obtener el token:', error);
      
      // Verifica que error es del tipo de axios
      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.error('Datos de la respuesta de error:', error.response.data);
          console.error('Estado de la respuesta de error:', error.response.status);
        } else if (error.request) {
          console.error('La solicitud se realizó pero no se recibió respuesta');
        }
      } else {
        console.error('Error al configurar la solicitud:', error instanceof Error ? error.message : String(error));
      }
      
      throw new Error('No se pudo obtener el token del formulario de pago');
    }
  }
} 